import type { OfflineQueueStore } from '@cuito/api-client';
import { useOfflineQueue } from '../offline/useOfflineQueue';

/**
 * 待上传队列（`05-ui-design-system.md` §3.3 的 `/cashier/offline`、§6.8 冻结 testid 的
 * `offline-queue-page` / `offline-queue-item` / `sync-progress`）。
 *
 * 三条冻结形态（逐字）：
 *   · 页根 `data-testid="offline-queue-page"`；空态 `没有待上传的记录`；按钮 `立即同步`；
 *   · 每条 `data-testid="offline-queue-item"` + `data-op-id` + `data-status`，
 *     徽标只有两个词：`待上传` / `上传失败`；
 *   · 同步中显示 `同步中…（N/M）`（`data-testid="sync-progress"`）。
 *
 * **失败的那条不删**（契约 §18.2 的「前端行为」列：`CONFLICT` / `NOT_FOUND` / `SKIPPED`
 * 要「保留本地记录供对账，显著提示店员」）⇒ 徽标转 `上传失败`，原因写在下面一行
 * `text-xs text-red-600`。同步会不会删条目由 `useOfflineQueue` 与服务端状态共同决定，
 * 页面这一层不做任何判断 —— 页面里出现「哪些算成功」的第二份判据，就是这份列表开始说谎的起点。
 *
 * ⚠️ **本机没有 IndexedDB 时不许显示空态**：「队列是空的」与「本机根本没有队列」是两句不同的话，
 * 后者显示成前者会让店员以为记录都上传了。这一句是本 Phase 唯一一处**未在冻结文案表里**的
 * 界面文字，出处是「不许静默」这条纪律本身（如实登记在交付报告里）。
 */
export interface CashierOfflinePageProps {
  /** 测试注入用；缺省是浏览器里那个 IndexedDB 队列（`undefined` = 用它） */
  store?: OfflineQueueStore | null;
}

export default function CashierOfflinePage({ store }: CashierOfflinePageProps) {
  const { supported, items, sync, syncing, progress } = useOfflineQueue(store);

  return (
    <section data-testid="offline-queue-page" className="flex flex-col gap-3 p-3">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-bold">待上传队列</h1>
        <button
          type="button"
          onClick={() => void sync()}
          disabled={syncing || !supported}
          className="min-h-[44px] rounded-lg border-2 border-black px-4 font-bold disabled:bg-gray-200 disabled:text-gray-400"
        >
          立即同步
        </button>
      </header>

      {syncing && (
        <p data-testid="sync-progress" className="text-sm text-gray-500">
          同步中…（{progress.done}/{progress.total}）
        </p>
      )}

      {!supported ? (
        <p className="py-12 text-center text-gray-500">本机不支持离线队列，离线期间的记录无法保存在本机</p>
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-gray-500">没有待上传的记录</p>
      ) : (
        items.map((item) => (
          <div
            key={item.id}
            data-testid="offline-queue-item"
            data-op-id={item.id}
            data-status={item.status}
            data-op-type={item.type ?? ''}
            data-order-no={item.orderNo ?? ''}
            className="rounded-lg border-2 border-black p-3"
          >
            <span className="text-xs font-bold">{item.status === 'FAILED' ? '上传失败' : '待上传'}</span>
            {item.message !== undefined && <p className="text-xs text-red-600">{item.message}</p>}
          </div>
        ))
      )}
    </section>
  );
}
